import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, SimpleGrid, Select, TextInput, Button, Loader, Center, Table, Badge,
} from '@mantine/core';
import { LineChart, BarChart, DonutChart } from '@mantine/charts';
import { IconStats } from '../../components/icons/CncIcons';
import { api } from '../../api/client';

interface Stats {
  filters: Record<string, unknown>;
  kpis: {
    totalOrders: number; activeOrders: number; finalizedOrders: number; cancelledOrders: number;
    cancellationRate: number; quotedOrders: number; totalRevenue: number; totalCost: number;
    totalMargin: number; avgMarginPct: number; avgOrderValue: number; totalLabourHours: number; avgHoursPerOrder: number;
  };
  charts: {
    revenueMarginByMonth: Array<{ month: string; revenue: number; margin: number }>;
    intakeByMonth: Array<{ month: string; count: number }>;
    byState: Array<{ state: string; count: number }>;
    byStatus: Array<{ status: string; count: number }>;
    topCustomers: Array<{ name: string; revenue: number; orders: number; marginPct: number }>;
    hoursByTechnician: Array<{ name: string; hours: number }>;
    costComposition: Array<{ bucket: string; cost: number }>;
    equipment: Array<{ name: string; hours: number; cost: number }>;
    consumables: Array<{ name: string; qty: number; cost: number }>;
    quotationFunnel: Array<{ status: string; count: number }>;
    leadTimeByStep: Array<{ step: string; avgDays: number; n: number }>;
  };
}

const eur = (n: number) => '€' + (n ?? 0).toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const eur2 = (n: number) => '€' + (n ?? 0).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATE_COLORS: Record<string, string> = { FINALIZED: 'gray.5', ONGOING: 'green.6', TECHNICAL_DIFFICULTY: 'red.6', CANCELLED: 'dark.5' };

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Paper p="md" radius="md" bg="var(--mantine-color-default-hover)">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={600} fz={24} style={{ lineHeight: 1.2 }}>{value}</Text>
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
    </Paper>
  );
}

function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>{title}</Title>
        {subtitle && <Text size="xs" c="dimmed">{subtitle}</Text>}
      </Group>
      {children}
    </Paper>
  );
}

export default function StatisticsPage() {
  const [customers, setCustomers] = useState<Array<{ value: string; label: string }>>([]);
  const [technicians, setTechnicians] = useState<Array<{ value: string; label: string }>>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [technicianId, setTechnicianId] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [data, setData] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [cs, ts] = await Promise.all([
          api.get<Array<{ id: number; name: string }>>('/customers'),
          api.get<Array<{ id: number; name: string }>>('/technicians'),
        ]);
        setCustomers(cs.map(c => ({ value: String(c.id), label: c.name })));
        setTechnicians(ts.map(t => ({ value: String(t.id), label: t.name })));
      } catch { /* non-fatal — filters just stay empty */ }
    })();
  }, []);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (customerId) p.set('customerId', customerId);
    if (technicianId) p.set('technicianId', technicianId);
    if (state) p.set('state', state);
    if (status) p.set('status', status);
    return p.toString();
  }, [from, to, customerId, technicianId, state, status]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<Stats>(`/stats${qs ? '?' + qs : ''}`)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [qs]);

  function reset() {
    setFrom(''); setTo(''); setCustomerId(null); setTechnicianId(null); setState(null); setStatus(null);
  }

  const k = data?.kpis;
  const c = data?.charts;

  return (
    <Stack>
      <Group gap="sm">
        <IconStats size={28} />
        <Title order={2}>Statistics</Title>
      </Group>

      {/* Filters */}
      <Paper data-tour="stats-filters" withBorder p="md" radius="md">
        <Group align="end" gap="sm" wrap="wrap">
          <TextInput label="From" type="date" value={from} onChange={e => setFrom(e.currentTarget.value)} w={150} />
          <TextInput label="To" type="date" value={to} onChange={e => setTo(e.currentTarget.value)} w={150} />
          <Select label="Customer" placeholder="All" data={customers} value={customerId} onChange={setCustomerId} clearable searchable w={200} />
          <Select label="Technician" placeholder="All" data={technicians} value={technicianId} onChange={setTechnicianId} clearable searchable w={180} />
          <Select label="State" placeholder="All" data={['FINALIZED', 'ONGOING', 'TECHNICAL_DIFFICULTY', 'CANCELLED']} value={state} onChange={setState} clearable w={170} />
          <Select label="Status" placeholder="All" data={['Order Set-up', 'In Quotation', 'Order Started', 'Supplying Consumables', 'In Execution', 'Preparing to Ship', 'Shipped', 'Delivered']} value={status} onChange={setStatus} clearable w={180} />
          <Button data-tour="stats-reset" variant="default" onClick={reset}>Reset</Button>
        </Group>
      </Paper>

      {loading || !k || !c ? (
        <Center h={300}><Loader /></Center>
      ) : (
        <>
          {/* KPI cards */}
          <SimpleGrid data-tour="stats-kpis" cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
            <Kpi label="Total orders" value={String(k.totalOrders)} hint={`${k.activeOrders} active · ${k.finalizedOrders} finalized`} />
            <Kpi label="Total revenue" value={eur(k.totalRevenue)} hint={`${k.quotedOrders} quoted orders`} />
            <Kpi label="Total margin" value={eur(k.totalMargin)} hint={`${k.avgMarginPct}% avg margin`} />
            <Kpi label="Avg order value" value={eur(k.avgOrderValue)} />
            <Kpi label="Work hours (labour)" value={`${k.totalLabourHours} h`} hint={`${k.avgHoursPerOrder} h / order`} />
            <Kpi label="Cancellation rate" value={`${k.cancellationRate}%`} hint={`${k.cancelledOrders} cancelled`} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <ChartCard title="Revenue & margin over time" subtitle="by order month">
              {c.revenueMarginByMonth.length ? (
                <LineChart h={260} data={c.revenueMarginByMonth} dataKey="month"
                  valueFormatter={(v) => eur(v)}
                  series={[{ name: 'revenue', color: 'orange.6' }, { name: 'margin', color: 'teal.6' }]} curveType="linear" withLegend />
              ) : <Text c="dimmed" size="sm">No data.</Text>}
            </ChartCard>

            <ChartCard title="Order intake" subtitle="orders created per month">
              {c.intakeByMonth.length ? (
                <BarChart h={260} data={c.intakeByMonth} dataKey="month" series={[{ name: 'count', color: 'blue.6' }]} />
              ) : <Text c="dimmed" size="sm">No data.</Text>}
            </ChartCard>

            <ChartCard title="Order state split">
              {c.byState.length ? (
                <Center>
                  <DonutChart h={260} withLabels chartLabel={`${k.totalOrders} orders`}
                    data={c.byState.map(s => ({ name: s.state.replace('_', ' '), value: s.count, color: STATE_COLORS[s.state] ?? 'gray.5' }))} />
                </Center>
              ) : <Text c="dimmed" size="sm">No data.</Text>}
            </ChartCard>

            <ChartCard title="Pipeline by status" subtitle="non-cancelled orders">
              <BarChart h={260} data={c.byStatus} dataKey="status" series={[{ name: 'count', color: 'grape.6' }]}
                xAxisProps={{ angle: -30, textAnchor: 'end', height: 70, interval: 0, fontSize: 10 }} />
            </ChartCard>

            <ChartCard title="Top customers by revenue">
              {c.topCustomers.length ? (
                <BarChart h={260} data={c.topCustomers} dataKey="name" orientation="vertical"
                  valueFormatter={(v) => eur(v)} series={[{ name: 'revenue', color: 'orange.6' }]}
                  yAxisProps={{ width: 120, fontSize: 11 }} />
              ) : <Text c="dimmed" size="sm">No data.</Text>}
            </ChartCard>

            <ChartCard title="Labour hours by technician">
              {c.hoursByTechnician.length ? (
                <BarChart h={260} data={c.hoursByTechnician} dataKey="name" series={[{ name: 'hours', color: 'teal.6' }]} />
              ) : <Text c="dimmed" size="sm">No data.</Text>}
            </ChartCard>

            <ChartCard title="Cost composition" subtitle="accepted quotations">
              <BarChart h={260} data={c.costComposition} dataKey="bucket" valueFormatter={(v) => eur(v)} series={[{ name: 'cost', color: 'indigo.6' }]} />
            </ChartCard>

            <ChartCard title="Average lead time by step" subtitle="calendar days">
              {c.leadTimeByStep.length ? (
                <BarChart h={260} data={c.leadTimeByStep} dataKey="step" series={[{ name: 'avgDays', color: 'cyan.7' }]}
                  xAxisProps={{ angle: -30, textAnchor: 'end', height: 80, interval: 0, fontSize: 10 }} />
              ) : <Text c="dimmed" size="sm">No completed steps in range.</Text>}
            </ChartCard>
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <ChartCard title="Top equipment by runtime">
              {c.equipment.length ? (
                <Table striped withTableBorder>
                  <Table.Thead><Table.Tr><Table.Th>Equipment</Table.Th><Table.Th ta="right">Hours</Table.Th><Table.Th ta="right">Cost</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>{c.equipment.map((e, i) => (
                    <Table.Tr key={i}><Table.Td>{e.name}</Table.Td><Table.Td ta="right">{e.hours}</Table.Td><Table.Td ta="right">{eur2(e.cost)}</Table.Td></Table.Tr>
                  ))}</Table.Tbody>
                </Table>
              ) : <Text c="dimmed" size="sm">No equipment usage in range.</Text>}
            </ChartCard>

            <ChartCard title="Top consumables">
              {c.consumables.length ? (
                <Table striped withTableBorder>
                  <Table.Thead><Table.Tr><Table.Th>Consumable</Table.Th><Table.Th ta="right">Qty</Table.Th><Table.Th ta="right">Cost</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>{c.consumables.map((x, i) => (
                    <Table.Tr key={i}><Table.Td>{x.name}</Table.Td><Table.Td ta="right">{x.qty}</Table.Td><Table.Td ta="right">{eur2(x.cost)}</Table.Td></Table.Tr>
                  ))}</Table.Tbody>
                </Table>
              ) : <Text c="dimmed" size="sm">No consumable usage in range.</Text>}
            </ChartCard>

            <ChartCard title="Quotation funnel" subtitle="all quotations in range">
              <Group gap="xs" wrap="wrap">
                {c.quotationFunnel.length ? c.quotationFunnel.map((q, i) => (
                  <Badge key={i} size="lg" variant="light" color={q.status === 'ACCEPTED' ? 'green' : q.status === 'REFUSED' ? 'red' : 'gray'}>
                    {q.status}: {q.count}
                  </Badge>
                )) : <Text c="dimmed" size="sm">No quotations in range.</Text>}
              </Group>
            </ChartCard>

            <ChartCard title="Customer leaderboard">
              {c.topCustomers.length ? (
                <Table striped withTableBorder>
                  <Table.Thead><Table.Tr><Table.Th>Customer</Table.Th><Table.Th ta="right">Revenue</Table.Th><Table.Th ta="right">Orders</Table.Th><Table.Th ta="right">Margin %</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>{c.topCustomers.map((x, i) => (
                    <Table.Tr key={i}><Table.Td>{x.name}</Table.Td><Table.Td ta="right">{eur2(x.revenue)}</Table.Td><Table.Td ta="right">{x.orders}</Table.Td><Table.Td ta="right">{x.marginPct}%</Table.Td></Table.Tr>
                  ))}</Table.Tbody>
                </Table>
              ) : <Text c="dimmed" size="sm">No revenue in range.</Text>}
            </ChartCard>
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}
